import {
  DEFAULT_PRIORITY_COLORS,
  type RingMode,
  type RingColors,
  type StageStatusColors,
  type PriorityColors as PriorityColorsDef,
} from "@/features/settings/stores/managePreferencesSlice";

export const getPriorityColors = (
  priority: string,
  customColors?: PriorityColorsDef
) => {
  const normalisedPriority = priority?.toLowerCase() ?? "";
  // The Prisma `Priorities` enum is `high | medium | low | none`, but the
  // UI/settings label for the lowest tier is "Cold" (stored under the `cold`
  // key in PriorityColors). Treat both `low` and `cold` as the same bucket so
  // leads stored as "low" still render their ring color.
  const isHigh = normalisedPriority.includes("high");
  const isMedium = normalisedPriority.includes("medium");
  const isCold =
    normalisedPriority.includes("cold") || normalisedPriority.includes("low");

  // Use the hex defaults (NOT the CSS variables) when no customColors are
  // passed. The `--color-medium` CSS variable is re-mapped by `@theme inline`
  // in `styles/index.css` to the much lighter `--color-track`, which made the
  // medium-priority ring nearly invisible against the gray avatar.
  const colors = customColors ?? DEFAULT_PRIORITY_COLORS;

  if (isHigh) return { border: colors.high, dot: colors.high };
  if (isMedium) return { border: colors.medium, dot: colors.medium };
  if (isCold) return { border: colors.cold, dot: colors.cold };
  return null;
};

export interface RingResult {
  border: string;
  dot: string;
}

export function getRingColor(opts: {
  ringMode: RingMode;
  priority?: string;
  lastCallStatus?: string;
  isQualifyingStatus?: boolean;
  isRejectionStatus?: boolean;
  callActivityColors: RingColors;
  stageStatusColors: StageStatusColors;
  priorityColors: PriorityColorsDef;
}): RingResult | null {
  const {
    ringMode,
    priority,
    lastCallStatus,
    isQualifyingStatus,
    isRejectionStatus,
    callActivityColors,
    stageStatusColors,
    priorityColors,
  } = opts;

  if (ringMode === "off") return null;

  if (ringMode === "priority") {
    return getPriorityColors(priority ?? "", priorityColors);
  }

  if (ringMode === "callActivity") {
    const status = lastCallStatus?.toLowerCase() ?? "";
    if (status.includes("missed"))
      return {
        border: callActivityColors.missedCall,
        dot: callActivityColors.missedCall,
      };
    if (status.includes("connected") || status.includes("answered"))
      return {
        border: callActivityColors.answeredCall,
        dot: callActivityColors.answeredCall,
      };
    if (status.includes("no_answer") || status.includes("no answer"))
      return {
        border: callActivityColors.noAnswer,
        dot: callActivityColors.noAnswer,
      };
    if (status.includes("incoming"))
      return {
        border: callActivityColors.incomingUnanswered,
        dot: callActivityColors.incomingUnanswered,
      };
    return null;
  }

  if (ringMode === "stageStatus") {
    if (isQualifyingStatus)
      return {
        border: stageStatusColors.qualifying,
        dot: stageStatusColors.qualifying,
      };
    if (isRejectionStatus)
      return {
        border: stageStatusColors.rejection,
        dot: stageStatusColors.rejection,
      };
    return null;
  }

  return null;
}
